import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { CheckIcon, HeartIcon } from '@/components/ui/icons'
import { Badge, Button, EarMark, Spinner } from '@/components/ui/primitives'
import { notify } from '@/components/ui/toast'
import { useT } from '@/i18n/use-t'
import type { Actress, Magnet, VideoCard as VideoCardData } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { useEngagement } from '@/lib/engagement'
import { formatBytes, shortDate, toneFor } from '@/lib/format'
import { useLocale } from '@/lib/queries'

export function Cover({
  code,
  imageUrl,
  className,
  label = true,
}: {
  code: string
  imageUrl: string | null
  className?: string
  label?: boolean
}) {
  const t = useT()
  const [failed, setFailed] = useState(false)
  const showImage = imageUrl && !failed
  return (
    <div
      className={cn('relative aspect-[3/2] overflow-hidden rounded-[14px]', className)}
      style={{ background: toneFor(code) }}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt={t('video.coverAlt', { code })}
          className="size-full object-contain"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : label ? (
        <span className="absolute bottom-2.5 left-3 font-mono text-xs text-moon/35">
          {t('video.coverFallback')} · {code}
        </span>
      ) : null}
    </div>
  )
}

export function Avatar({
  id,
  name,
  size = 48,
  ring = false,
  shape = 'circle',
}: {
  id: string
  name: string
  size?: number
  ring?: boolean
  shape?: 'circle' | 'portrait'
}) {
  return (
    <div
      role="img"
      aria-label={name}
      className={cn(
        'flex shrink-0 items-center justify-center text-sm text-haze',
        shape === 'circle' ? 'rounded-full' : 'rounded-2xl',
        ring ? 'border-2 border-magenta' : 'border border-line',
      )}
      style={{
        width: size,
        height: shape === 'circle' ? size : (size * 4) / 3,
        background: toneFor(id),
      }}
    >
      <span aria-hidden>{name.slice(-2)}</span>
    </div>
  )
}

export function FavoriteButton({
  videoId,
  favorited,
  variant = 'overlay',
}: {
  videoId: string
  favorited: boolean
  variant?: 'overlay' | 'plain' | 'wide'
}) {
  const t = useT()
  const { phase, setActive, verify } = useEngagement('favorite', videoId)
  const busy = phase === 'pending' || phase === 'verifying'
  const label =
    phase === 'unknown' ? t('actress.unknown') : favorited ? t('video.unfavorite') : t('video.favorite')
  const onClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (phase === 'unknown') void verify()
    else void setActive(!favorited)
  }
  if (variant === 'wide')
    return (
      <Button
        aria-pressed={favorited}
        onClick={onClick}
        loading={busy}
        className={cn(
          'flex-1',
          favorited && 'border-magenta bg-magenta-bg text-rose-soft',
          phase === 'unknown' && 'border-amber bg-amber-bg text-amber-text',
        )}
      >
        {!busy ? <HeartIcon size={18} filled={favorited} /> : null}
        {phase === 'unknown' ? t('actress.unknown') : favorited ? t('video.favorited') : t('video.favorite')}
      </Button>
    )
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={favorited}
      title={label}
      onClick={onClick}
      className={cn(
        'inline-flex size-11 items-center justify-center rounded-[10px] transition-colors',
        variant === 'overlay' ? 'bg-ink/75 hover:bg-ink' : 'hover:bg-dusk',
        favorited ? 'text-magenta' : 'text-moon',
        phase === 'unknown' && 'text-amber',
      )}
    >
      {busy ? <Spinner /> : <HeartIcon size={18} filled={favorited} />}
    </button>
  )
}

export function VideoCard({ video, showActress = true }: { video: VideoCardData; showActress?: boolean }) {
  const t = useT()
  const locale = useLocale()
  return (
    <article className="group relative flex flex-col gap-2.5" data-anchor={video.id}>
      <div className="relative">
        <Link
          to="/$locale/videos/$code"
          params={{ locale, code: video.code }}
          className="block rounded-[14px] focus-visible:outline-offset-4"
        >
          <Cover
            code={video.code}
            imageUrl={video.imageUrl}
            className="transition-[filter] group-hover:brightness-110"
          />
          <span className="sr-only">
            {video.code} {video.title}
          </span>
        </Link>
        {video.isNew ? (
          <Badge tone="new" className="absolute top-2 left-2">
            {t('video.new')}
          </Badge>
        ) : null}
        <div className="absolute top-1.5 right-1.5">
          <FavoriteButton videoId={video.id} favorited={video.favorited} />
        </div>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-mono text-sm font-semibold text-rose">{video.code}</span>
        {showActress ? (
          <span className="truncate text-xs text-haze">{video.actresses.map((a) => a.name).join(' / ')}</span>
        ) : (
          <span className="font-mono text-xs text-faint">
            {video.releaseDate ? shortDate(video.releaseDate) : '—'}
          </span>
        )}
      </div>
      <p className="line-clamp-2 min-h-[2lh] text-sm leading-normal text-moon">{video.title}</p>
      <div className="flex flex-wrap gap-1.5">
        {video.magnetCount > 0 ? (
          <Badge tone="magnet">{t('video.magnets', { count: video.magnetCount })}</Badge>
        ) : (
          <Badge tone="muted">{t('video.noMagnets')}</Badge>
        )}
        {video.hasTrailer ? <Badge tone="trailer">{t('video.trailer')}</Badge> : null}
      </div>
    </article>
  )
}

export function VideoRow({ video }: { video: VideoCardData }) {
  const t = useT()
  const locale = useLocale()
  return (
    <article className="flex items-center gap-3" data-anchor={video.id}>
      <Link
        to="/$locale/videos/$code"
        params={{ locale, code: video.code }}
        className="relative w-[138px] shrink-0 rounded-xl"
      >
        <Cover code={video.code} imageUrl={video.imageUrl} className="rounded-xl" label={false} />
        {video.isNew ? (
          <Badge tone="new" className="absolute top-1.5 left-1.5 text-[11px]">
            {t('video.new')}
          </Badge>
        ) : null}
      </Link>
      <Link
        to="/$locale/videos/$code"
        params={{ locale, code: video.code }}
        className="flex min-w-0 flex-1 flex-col gap-1 text-moon hover:text-moon"
      >
        <span className="font-mono text-sm font-semibold text-rose">{video.code}</span>
        <span className="line-clamp-2 text-[13px] leading-snug">{video.title}</span>
        <span className="flex items-center gap-1.5 text-xs text-haze">
          <span className="truncate">{video.actresses[0]?.name}</span>
          {video.magnetCount > 0 ? (
            <Badge tone="magnet" className="text-[11px]">
              {t('video.magnets', { count: video.magnetCount })}
            </Badge>
          ) : null}
        </span>
      </Link>
      <FavoriteButton videoId={video.id} favorited={video.favorited} variant="plain" />
    </article>
  )
}

export function SubscribeButton({
  actressId,
  subscribed,
  size = 'sm',
  className,
}: {
  actressId: string
  subscribed: boolean
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const t = useT()
  const { phase, setActive, verify } = useEngagement('subscription', actressId)
  if (phase === 'unknown')
    return (
      <Button
        size={size}
        onClick={() => void verify()}
        className={cn('min-w-[92px] border-amber bg-amber-bg text-amber-text', className)}
      >
        {t('actress.unknown')}
      </Button>
    )
  if (phase === 'pending' || phase === 'verifying')
    return (
      <Button
        size={size}
        disabled
        className={cn('min-w-[92px] border-dashed border-dim bg-transparent text-haze', className)}
      >
        <Spinner />
        {t('actress.pending')}
      </Button>
    )
  return subscribed ? (
    <Button
      size={size}
      aria-pressed
      onClick={() => void setActive(false)}
      className={cn('min-w-[92px]', className)}
      title={t('actress.unsubscribe')}
    >
      {t('actress.subscribed')}
      <CheckIcon size={14} />
    </Button>
  ) : (
    <Button
      size={size}
      variant="primary"
      aria-pressed={false}
      onClick={() => void setActive(true)}
      className={cn('min-w-[92px]', className)}
    >
      {t('actress.subscribe')}
    </Button>
  )
}

export function ActressCard({ actress }: { actress: Actress }) {
  const t = useT()
  const locale = useLocale()
  return (
    <article className="flex flex-col gap-2.5" data-anchor={actress.id}>
      <Link
        to="/$locale/actresses/$id"
        params={{ locale, id: actress.id }}
        className="flex flex-col gap-2 text-moon hover:text-moon"
      >
        <EarMark active={actress.subscribed} className="self-center" />
        <div
          className={cn(
            'aspect-[3/4] rounded-2xl',
            actress.subscribed ? 'border-2 border-magenta' : 'border border-line',
          )}
          style={{ background: toneFor(actress.id) }}
          role="img"
          aria-label={actress.name}
        />
        <span className="truncate text-[15px] font-semibold">{actress.name}</span>
        <span className="truncate text-xs text-faint">
          {actress.aliases[0]} ·{' '}
          <span className="font-mono">
            {actress.videoCount === null ? '—' : t('common.videosCount', { count: actress.videoCount })}
          </span>
        </span>
      </Link>
      <SubscribeButton actressId={actress.id} subscribed={actress.subscribed} className="w-full" />
    </article>
  )
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export function useCopy() {
  const t = useT()
  return async (text: string, successLabel = t('common.copied')) => {
    const ok = await copyText(text)
    notify(ok ? successLabel : t('common.copyFailed'), ok ? 'success' : 'error')
    return ok
  }
}

export function pickBest(magnets: Magnet[], bestId: string | null): Magnet | null {
  return magnets.find((m) => m.id === bestId) ?? null
}

export function magnetMeta(m: Magnet) {
  return { size: formatBytes(m.size), seeders: m.seeders ?? '—', source: m.source }
}

export function CardSkeleton({ count = 10 }: { count?: number }) {
  return Array.from({ length: count }, (_, i) => (
    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
    <div key={i} className="flex flex-col gap-2.5" aria-hidden>
      <div className="skeleton aspect-[3/2] rounded-[14px]" />
      <div className="skeleton h-3.5 w-3/5 rounded-md" />
      <div className="skeleton h-3.5 w-11/12 rounded-md" />
    </div>
  ))
}
